import { useCallback } from 'react';
import { message } from 'antd';
import { getUserDetails, makePostCall, removeContentById } from '../../../utils/helper';
import { STATUS_MAP } from '../constants';
import { extractReportParts, extractMedicalContent, updateHeaderOnSignoff } from '../utils/reportParser';
import type { OrderDetails, Report } from '../types';

interface UseReportActionsOptions {
  orderDetails: OrderDetails | null;
  currentReport: Report | null;
  isSignedOff: boolean;
  showFooter: boolean;
  /** When header/footer live outside the editor iframe, pass their HTML
   *  so the save flow can store them without parsing editor content. */
  externalHeaderHtml?: string;
  externalFooterHtml?: string;
  onSaved?: (status: string) => void;
}

/**
 * Provides save, delete actions for the report editor.
 * Centralises the save flow:
 *   1. Strip cosign element
 *   2. Update header on signoff
 *   3. Extract header/body/footer
 *   4. POST to /submit-report
 */
export const useReportActions = ({
  orderDetails,
  currentReport,
  isSignedOff,
  showFooter,
  externalHeaderHtml,
  externalFooterHtml,
  onSaved,
}: UseReportActionsOptions) => {
  const pacs_order = orderDetails?.pacs_order;
  const patient = pacs_order?.patient;

  const saveReport = useCallback(async (
    editorContent: string,
    statusCode: string,
    extraPayload: Record<string, any> = {},
  ) => {
    if (!pacs_order || !patient || !editorContent) return;

    let contentToSave = removeContentById(editorContent, 'cosign');

    let header: string;
    let body: string;
    let footer: string;

    const isExternal = !!(externalHeaderHtml || externalFooterHtml);

    if (isExternal) {
      // External mode — header/footer are not inside editor content.
      header = externalHeaderHtml
        ? (statusCode === 'SIGNEDOFF' ? updateHeaderOnSignoff(externalHeaderHtml) : externalHeaderHtml)
        : '';
      footer = externalFooterHtml || '';
      // Editor content only contains the body (and possibly elements that
      // extractReportParts would strip), so extract to be safe.
      const parts = extractReportParts(contentToSave);
      body = parts.body;
    } else {
      // Internal mode — header/footer live inside editor content.
      if (statusCode === 'SIGNEDOFF') {
        contentToSave = updateHeaderOnSignoff(contentToSave);
      }
      const parts = extractReportParts(contentToSave);
      header = parts.header;
      body = parts.body;
      footer = parts.footer;
    }

    const backendStatus = STATUS_MAP[statusCode] || statusCode;

    await makePostCall('/submit-report', {
      html: body,
      header,
      footer: showFooter ? footer : '',
      yh_no: patient.pat_pin,
      order_no: pacs_order.po_his_ord_no,
      acc_no: pacs_order.po_acc_no,
      order_id: pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
      status: backendStatus,
      report_id: currentReport?.pr_id,
      is_editing_signedoff: isSignedOff,
      edited_at: isSignedOff ? new Date().toISOString() : undefined,
      edited_by: isSignedOff ? getUserDetails()?.username : undefined,
      ...extraPayload,
    });

    onSaved?.(backendStatus);
  }, [pacs_order, patient, currentReport, isSignedOff, showFooter, externalHeaderHtml, externalFooterHtml, onSaved]);

  const deleteReport = useCallback(async (report: Report) => {
    if (!patient) return;
    await makePostCall('/delete-report', {
      report_id: report.pr_id,
      yh_no: patient.pat_pin,
      order_id: report.pr_pacs_ord_id,
    });
  }, [patient]);

  const printReport = useCallback(async (editorContent: string) => {
    if (!pacs_order || !patient || !editorContent) return;

    try {
      const medicalContent = extractMedicalContent(editorContent);
      const res = await makePostCall('/print-report', {
        report: currentReport,
        patDetails: orderDetails,
        html: medicalContent,
        order_id: pacs_order.pacs_ord_id,
      }, {
        responseType: 'arraybuffer',
      });

      const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.print();
      }
    } catch (err) {
      console.error('Failed to print report:', err);
      message.error('Failed to print report');
    }
  }, [pacs_order, patient, currentReport, orderDetails]);

  return { saveReport, deleteReport, printReport };
};
